import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserPlus, Edit2, Users, ShieldCheck } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Pagination from "../../../components/Pagination/Pagination";
import Notification from "../../../components/Notification/Notification";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES & UTILS ===================== */
import { apiGet } from "../../../apiService";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./RolesPermissions.module.scss";

const RolesPermissions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Filters
  const [filterRoleName, setFilterRoleName] = useState("");
  const [filterRoleType, setFilterRoleType] = useState("");

  const [roleTypes, setRoleTypes] = useState([]);
  const [allRoleNames, setAllRoleNames] = useState([]);

  /* ===================== NOTIFICATION ===================== */
  const showNotification = (msg, type = "success") => {
    setNotification(msg);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* Success notice passed back from the Add/Edit Role page */
  useEffect(() => {
    if (location.state?.notice) {
      showNotification(location.state.notice, "success");
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===================== FETCH ROLES ===================== */
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: rowsPerPage,
      });

      if (filterRoleName) params.append("name", filterRoleName);
      if (filterRoleType) params.append("roleType", filterRoleType);

      const response = await apiGet("user", `/roles?${params.toString()}`);

      if (response?.success) {
        const rolesArray = Array.isArray(response.data) ? response.data : [];

        const formattedRoles = rolesArray.map((role) => ({
          id: role.id,
          name: role.name || "—",
          keyname: role.roleType || "—",
          users: role.userAssignCount ?? 0,
          onView: () => navigate(`/admin/role-permission/edit/${role.id}`),
        }));

        setRoles(formattedRoles);
        if (allRoleNames.length === 0) {
          setAllRoleNames(response.roleNames || []);
        }

        setRoleTypes(response.roleTypes || []);
        setPagination(response.pagination);
      }
    } catch (err) {
      if (err?.message?.includes("403")) {
        navigate("/page-not-found", { replace: true });
        return;
      }
      setError("Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, filterRoleName, filterRoleType, navigate]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterRoleName, filterRoleType]);

  /* ===================== TABLE COLUMNS ===================== */
  const columns = [
    { label: "Role Name", key: "name" },
    { label: "Type", key: "keyname" },
    { label: "Users Assigned", key: "users" },
    { label: "Actions", key: "action" },
  ];

  /* ===================== JSX ===================== */
  return (
    <div className={styles["admin-roles-per__page"]}>
      {notification && (
        <div className={styles["admin-roles-per__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-roles-per__header"]}>
        <div className={styles["admin-roles-per__icon-wrapper"]}>
          <ShieldCheck size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-roles-per__title-group"]}>
          <h2 className={styles["admin-roles-per__title"]}>
            Roles & Permissions
          </h2>
          <p className={styles["admin-roles-per__subtitle"]}>
            Manage access and control permissions for users and customers.
          </p>
        </div>

        <div className={styles["admin-roles-per__actions"]}>
          {can("role-permission", "create") && (
            <Button
              text="Add Role"
              icon={UserPlus}
              onClick={() => navigate("/admin/role-permission/create")}
            />
          )}
        </div>
      </div>

      {/* MAIN CARD */}
      <div className={styles["admin-roles-per__card"]}>
        <div className={styles["admin-roles-per__controls"]}>
          <div className={styles["admin-roles-per__showing-text"]}>
            Showing {roles.length} of {pagination.total} roles
          </div>

          <div className={styles["admin-roles-per__filters"]}>
            <Input
              type="select"
              placeholder="All Roles"
              value={filterRoleName}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterRoleName(e.target.value);
              }}
              options={[
                { label: "All Roles", value: "" },
                ...allRoleNames.map((name) => ({ label: name, value: name })),
              ]}
            />
            <Input
              type="select"
              placeholder="All Types"
              value={filterRoleType}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterRoleType(e.target.value);
              }}
              options={[
                { label: "All Types", value: "" },
                ...roleTypes.map((type) => ({ label: type, value: type })),
              ]}
            />
          </div>
        </div>

        {error && <p style={{ color: "#d54747", fontWeight: 600 }}>{error}</p>}

        {loading ? (
          <TableSkeleton columns={4} rows={5} />
        ) : (
          !error && (
            <APITable
              columns={columns}
              data={roles}
              renderCell={(row, key) => {
                if (key === "keyname") {
                  return (
                    <span
                      className={`${styles["admin-roles-per__badge"]} ${
                        row.keyname === "Internal"
                          ? styles["admin-roles-per__badge--internal"]
                          : styles["admin-roles-per__badge--client"]
                      }`}
                    >
                      {row.keyname}
                    </span>
                  );
                }

                if (key === "users") {
                  return (
                    <div className={styles["admin-roles-per__users-cell"]}>
                      <Users size={16} strokeWidth={2.5} />
                      <span>{row.users}</span>
                    </div>
                  );
                }

                if (key === "action") {
                  if (!can("role-permission", "update")) return "—";
                  return (
                    <div
                      className={styles["admin-roles-per__icon-btn"]}
                      onClick={row.onView}
                      data-tooltip="Edit Role"
                    >
                      <Edit2 size={18} strokeWidth={2.5} />
                    </div>
                  );
                }
                return row[key];
              }}
            />
          )
        )}

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />
      </div>

    </div>
  );
};

export default RolesPermissions;
